import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { setModalId, usePublicState } from '../../store/slices/publicSlice';
import { setFilters, useDispute } from '../../store/slices/disputeSlice';

import CustomModal from '../atoms/CustomModal';
import FormField from '../FormField/FormField';
import Button from '../atoms/Button';
import InfoText from '../atoms/InfoText';
import { useGetDisputeStatuesLKQuery } from '../../store/services/disputeService';

interface FormValues {
  customerType: string[];
  status: string[];
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

export default function FiltersPopup() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modalId } = usePublicState();
  const filters = useDispute();

  const { data: disputeStatus, isLoading: isStatusLoading } =
    useGetDisputeStatuesLKQuery({});

  const schema = z
    .object({
      customerType: z.array(z.string()),
      status: z.array(z.string()),
      fromDate: z.custom<Dayjs>().nullable(),
      toDate: z.custom<Dayjs>().nullable(),
    })
    .superRefine(({ fromDate, toDate }, ctx) => {
      if (fromDate && toDate && toDate.isBefore(fromDate, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('dispute.dateToBeforeFrom'),
          path: ['toDate'],
        });
      }
    });

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      customerType: [],
      status: [],
      fromDate: null,
      toDate: null,
    },
  });

  const onClose = () => dispatch(setModalId(null));

  // Sync form values with Redux state when modal opens
  useEffect(() => {
    if (modalId === 'DISPUTE_FILTERS') {
      let buyerSeller: string[] = [];
      if (Array.isArray(filters.customerType)) {
        buyerSeller = filters.customerType;
      } else if (filters.customerType) {
        buyerSeller = [filters.customerType];
      }

      methods.reset({
        customerType: buyerSeller,
        status: filters.status || [],
        fromDate: filters.fromDate ? dayjs(filters.fromDate) : null,
        toDate: filters.toDate ? dayjs(filters.toDate) : null,
      });
    }
  }, [modalId, filters]);

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    watchAll.customerType && watchAll.customerType.length > 0,
    !!watchAll.status?.length,
    watchAll.fromDate,
    watchAll.toDate,
  ].filter(Boolean).length;

  const onReset = () => {
    methods.reset({
      customerType: [],
      status: [],
      fromDate: null,
      toDate: null,
    });
  };

  const onSubmit = (values: FormValues) => {
    const fromDateStr = values.fromDate
      ? values.fromDate.format('YYYY-MM-DD')
      : '';
    const toDateStr = values.toDate ? values.toDate.format('YYYY-MM-DD') : '';

    dispatch(
      setFilters({
        customerType: values.customerType.length > 0 ? values.customerType : '',
        status: values.status || [],
        fromDate: fromDateStr,
        toDate: toDateStr,
        page: 0,
      })
    );
    onClose();
  };

  const statusOptions =
    disputeStatus?.payload?.map((status) => ({
      label: status.name,
      value: status.id,
    })) || [];

  return (
    <CustomModal
      title={t('home.filters')}
      isOpen={modalId === 'DISPUTE_FILTERS'}
      onClose={onClose}
      subTitle={
        <div className="flex items-center gap-2">
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied')}`}
          />
          <button
            type="button"
            onClick={onReset}
            className="text-light-success hover:underline text-sm font-semibold"
          >
            {t('home.resetFilter')}
          </button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          {/* User Type Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base text-[#141414]">
                  {t('home.customerType')}
                </h3>
                <p className="text-xs text-[#8A8A8A]">
                  {t('home.customerTypeDesc')}
                </p>
              </div>
            </div>
            <FormField
              name="customerType"
              id="customerType"
              type="checkBox"
              options={[
                { label: t('home.businessBuyer'), value: 'BUYER' },
                { label: t('home.businessSeller'), value: 'SELLER' },
              ]}
              className="[&.ant-checkbox-group]:flex [&.ant-checkbox-group]:gap-x-12"
            />
          </div>

          <hr className="border-t border-[#F0F0F0]" />

          {/* Status Section */}
          <div className="space-y-2">
            <h3 className="font-semibold text-base text-[#141414]">
              {t('dispute.status')}
            </h3>
            <FormField
              name="status"
              id="status"
              type="select"
              placeholder={t('home.selectStatus')}
              options={statusOptions}
              loading={isStatusLoading}
              mode="multiple"
              allowClear
            />
          </div>

          <hr className="border-t border-[#F0F0F0]" />

          {/* Date Range Section */}
          <div className="grid grid-cols-2 gap-x-4">
            <FormField
              name="fromDate"
              id="fromDate"
              type="date"
              label={t('home.dateFrom')}
              placeholder="dd/mm/yyyy"
            />
            <FormField
              name="toDate"
              id="toDate"
              type="date"
              label={t('home.dateTo')}
              placeholder="dd/mm/yyyy"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-x-4 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[120px] h-[48px]"
            >
              {t('home.cancel')}
            </Button>
            <Button
              type="submit"
              variant="dark"
              className="min-w-[120px] h-[48px]"
            >
              {t('home.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
